import React from "react";
import {
  FiMail,
  FiMaximize2,
  FiPhone,
  FiUser,
} from "react-icons/fi";
import { HiOfficeBuilding, HiUserGroup } from "react-icons/hi";

import Reveal from "../../../components/ui/Reveal";

import {
  cn,
  FALLBACK_TROMBINO_PHOTO,
} from "../utils/trombinosUtils";

function RoleBadge({ isElected }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold",
        isElected
          ? "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
          : "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300"
      )}
    >
      <span
        className={cn(
          "h-2 w-2 rounded-full",
          isElected ? "bg-amber-500" : "bg-sky-500"
        )}
      />
      {isElected ? "Conseil municipal" : "Service municipal"}
    </span>
  );
}

function InfoBadge({ icon: Icon, label }) {
  if (!label) return null;

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

export default function TrombinoCard({ person, onOpenPhoto, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <article
        className={cn(
          "group h-full overflow-hidden rounded-[2rem] border bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl dark:bg-slate-950",
          person.isElected
            ? "border-amber-200 hover:border-amber-300 dark:border-amber-500/20"
            : "border-slate-200 hover:border-[#1277B9]/50 dark:border-slate-800"
        )}
      >
        <div
          className={cn(
            "h-2 bg-gradient-to-r",
            person.isElected
              ? "from-amber-500 via-orange-400 to-rose-400"
              : "from-[#1277B9] via-sky-400 to-cyan-300"
          )}
        />

        <div className="grid gap-5 p-5 sm:grid-cols-[138px_1fr] sm:p-6">
          <div className="flex justify-center sm:justify-start">
            <button
              type="button"
              onClick={() => onOpenPhoto?.(person)}
              className="group/photo relative rounded-[1.6rem] focus:outline-none focus:ring-4 focus:ring-[#1277B9]/20"
            >
              <div className="relative h-48 w-36 overflow-hidden rounded-[1.6rem] border border-slate-200 bg-slate-100 p-1 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <div className="h-full w-full overflow-hidden rounded-[1.25rem] bg-slate-200 dark:bg-slate-800">
                  <img
                    src={person.photo}
                    alt={person.fullName}
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition duration-500 group-hover/photo:scale-105"
                    onError={(event) => {
                      event.currentTarget.src = FALLBACK_TROMBINO_PHOTO;
                    }}
                  />
                </div>

                <div className="pointer-events-none absolute inset-1 flex items-center justify-center rounded-[1.25rem] bg-slate-950/0 opacity-0 transition group-hover/photo:bg-slate-950/45 group-hover/photo:opacity-100">
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/75 px-3 py-1.5 text-xs font-bold text-white">
                    <FiMaximize2 className="h-3.5 w-3.5" />
                    Agrandir
                  </div>
                </div>
              </div>

              {person.isElected && (
                <span className="absolute -bottom-2 left-1/2 inline-flex -translate-x-1/2 whitespace-nowrap rounded-full border border-amber-200 bg-white px-3 py-1 text-[11px] font-bold text-amber-700 shadow-sm dark:border-amber-500/20 dark:bg-slate-950 dark:text-amber-300">
                  Élu municipal
                </span>
              )}
            </button>
          </div>

          <div className="min-w-0 pt-1">
            <div className="flex flex-wrap items-center gap-2">
              <RoleBadge isElected={person.isElected} />
              <InfoBadge icon={HiOfficeBuilding} label={person.serviceLabel} />
              <InfoBadge icon={HiUserGroup} label={person.groupe} />
            </div>

            <h2 className="mt-4 text-2xl font-bold leading-tight text-slate-950 dark:text-white">
              {person.fullName}
            </h2>

            {person.fonction && (
              <p className="mt-2 text-sm font-bold leading-6 text-[#1277B9] dark:text-sky-300">
                {person.fonction}
              </p>
            )}

            {(person.mail || person.phone) && (
              <div className="mt-4 space-y-2 rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm dark:border-slate-800 dark:bg-slate-900/70">
                {person.mail && (
                  <a
                    href={`mailto:${person.mail}`}
                    className="flex items-start gap-2 font-semibold text-slate-600 transition hover:text-[#1277B9] hover:underline dark:text-slate-300"
                  >
                    <FiMail className="mt-0.5 h-4 w-4 shrink-0 text-[#1277B9]" />
                    <span className="break-all">{person.mail}</span>
                  </a>
                )}

                {person.phone && (
                  <a
                    href={person.phoneHref}
                    className="flex items-start gap-2 font-semibold text-slate-600 transition hover:text-[#1277B9] hover:underline dark:text-slate-300"
                  >
                    <FiPhone className="mt-0.5 h-4 w-4 shrink-0 text-[#1277B9]" />
                    <span>{person.phone}</span>
                  </a>
                )}
              </div>
            )}

            {person.presentation && (
              <p className="mt-4 line-clamp-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
                {person.presentation}
              </p>
            )}

            {!person.presentation && !person.mail && !person.phone && (
              <div className="mt-4 flex items-center gap-2 rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-500 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-400">
                <FiUser className="h-4 w-4" />
                Informations publiques limitées.
              </div>
            )}
          </div>
        </div>
      </article>
    </Reveal>
  );
}